import { useEffect, useState } from "react";
import Modal from "frontend/modal/modal";
import CreateCollaborationModal from "frontend/modals/collaboration/create-collaboration-modal";
import { Project } from "shared/datamodel/schemas/project";
import AddCanvasesToProjectModelNew from "frontend/modals/collaboration/project/add-canvases-to-project-modal-new";
import { CollaborationType, InviteMode } from "state-atoms/teams-atoms";
import { useProject } from "frontend/hooks/use-project";
import { Board, CanvasTemplate, MinimalBoard } from "shared/datamodel/schemas";
import RestrictedUserModal from "frontend/modals/restricted-user-new-modal";
import InviteModalNew from "frontend/modals/invite-modal-new";
import { InstanceType } from "shared/datamodel/schemas/ability";
import AppLoader from "frontend/loader/app-loader";
import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import { useBoardsCache } from "frontend/hooks/caching/use-boards-cache";
import { MY_BOARDS_SECTION_ID } from "shared/datamodel/schemas/board-section";

export function CreateProject({
  parentTeamId,
  createNewBoard,
  onEditSeats,
  setSelectedProject,
  onDismiss,
  setIsMovingBoards,
}: {
  parentTeamId: string;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onEditSeats: () => void;
  setSelectedProject: (projectId: string, urlId?: string) => void;
  onDismiss?: () => void;
  setIsMovingBoards: (value: boolean) => void;
}) {
  const [projectName, setProjectName] = useState("");
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(true);
  const [showInviteProjectMembersModal, setShowInviteProjectMembersModal] = useState(false);
  const [showAddCanvasesToProjectModel, setShowAddCanvasesToProjectModel] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState<boolean>(false);
  const [canvases, setCanvases] = useState<Board[]>([]);
  const [newCanvasForProject, setNewCanvasForProject] = useState<boolean>(false);
  const [createdProject, setCreatedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [boards, setBoards] = useState<MinimalBoard[]>([]);
  const { fetchMyBoards, getSection } = useBoardsCache();

  useEffect(() => {
    const myBoards = getSection(MY_BOARDS_SECTION_ID);
    setBoards(myBoards as MinimalBoard[]);
    if (myBoards?.length === 0) {
      // Only fetch if we don't have any boards in cache
      fetchMyBoards();
    }
  }, [getSection, fetchMyBoards]);

  const { addCanvasesToProjectNew } = useProject();
  const { createProject: handleCreateProject } = useProjectsCache();

  useEffect(() => {
    createdProject &&
      addCanvasesToProjectNew({
        projectId: createdProject?.documentId,
        canvases,
        teamId: parentTeamId,
        createNewBoard,
        newCanvasForProject,
        setIsMovingBoards,
      });
  }, [createdProject, canvases, newCanvasForProject, parentTeamId]);

  async function createProject({
    parentTeamId,
    name,
    isMovingBoards,
  }: {
    parentTeamId: string;
    name: string;
    isMovingBoards: boolean;
  }) {
    setIsMovingBoards(isMovingBoards);
    setIsLoading(true);
    const project = await handleCreateProject({ name, teamId: parentTeamId });
    setIsLoading(false);
    if (project) {
      setCreatedProject(project);
      setShowInviteProjectMembersModal(true);
      setProjectName(name);
      setSelectedProject(project.documentId, project.urlId);
    } else {
      setShowRestrictedModal(true);
    }
  }

  function onCloseModal() {
    setShowCreateProjectModal(false);
    setShowAddCanvasesToProjectModel(false);
    setShowInviteProjectMembersModal(false);
    setShowRestrictedModal(false);
    onDismiss?.();
  }

  function onCloseAddCanvasesToProjectModal() {
    setShowAddCanvasesToProjectModel(false);
  }

  return (
    <>
      {showCreateProjectModal && (
        <Modal dimBackground={true} onDismissOnEscape={onCloseModal} onClickBackground={onCloseModal}>
          <CreateCollaborationModal
            onDismiss={() => {
              setShowCreateProjectModal(false);
              onDismiss?.();
            }}
            onCreateProject={(name: string) => {
              setProjectName(name);
              setShowCreateProjectModal(false);
              if (boards.length > 0) {
                setShowAddCanvasesToProjectModel(true);
              } else {
                createProject({ parentTeamId, name, isMovingBoards: false });
              }
            }}
            type={CollaborationType.Project}
            subtitle={"People in your project have access to every canvas."}
            inputPlaceholder={"Name your new project"}
          />
        </Modal>
      )}
      {showAddCanvasesToProjectModel && (
        <Modal dimBackground={true} onDismissOnEscape={onCloseModal} onClickBackground={onCloseModal}>
          <AddCanvasesToProjectModelNew
            onDismiss={onCloseAddCanvasesToProjectModal}
            onCreateProject={(canvases: Board[], newCanvasForProject?: boolean) => {
              setCanvases(canvases);
              setNewCanvasForProject(newCanvasForProject ?? false);
              setShowAddCanvasesToProjectModel(false);
              setShowInviteProjectMembersModal(true);
              createProject({ parentTeamId, name: projectName, isMovingBoards: true });
            }}
            boards={boards as Board[]}
          />
        </Modal>
      )}
      {showInviteProjectMembersModal && createdProject && (
        <Modal dimBackground={true} onDismissOnEscape={onCloseModal} onClickBackground={onCloseModal}>
          <InviteModalNew
            name={projectName}
            onDismiss={() => {
              setShowInviteProjectMembersModal(false);
            }}
            inviteMode={InviteMode.Create}
            onEditSeats={onEditSeats}
            source="create-project"
            instance={createdProject}
            type={InstanceType.Project}
          />
        </Modal>
      )}
      {showRestrictedModal && (
        <RestrictedUserModal
          trackingEvent={"home"}
          onDismiss={() => {
            setShowRestrictedModal(false);
            onDismiss?.();
          }}
        />
      )}
      {isLoading && <AppLoader />}
    </>
  );
}
